import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createDossier } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

/**
 * Reusable dialog to create a Dossier without persisting until "Enregistrer" is clicked.
 * Props:
 *   - open, onOpenChange
 *   - prospect (optional): if provided, dossier will be linked & defaults pre-filled
 *   - onCreated: optional callback receiving the created dossier
 */
export const CreateDossierDialog = ({ open, onOpenChange, prospect, onCreated }) => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        titre: '',
        type_travaux: '',
        montant_devis: '',
        commentaire: '',
    });

    React.useEffect(() => {
        if (open) {
            setForm({
                titre: prospect ? `Dossier - ${prospect.prenom || ''} ${prospect.nom || ''}`.trim() : '',
                type_travaux: prospect?.type_travaux || prospect?.nature_projet || '',
                montant_devis: '',
                commentaire: '',
            });
        }
    }, [open, prospect]);

    const handleSave = async (andOpen = true) => {
        if (!form.titre.trim()) {
            toast.error('Le titre du dossier est requis');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                titre: form.titre.trim(),
                type_travaux: form.type_travaux || undefined,
                montant_devis: form.montant_devis ? Number(form.montant_devis) : undefined,
                commentaire: form.commentaire || undefined,
            };
            if (prospect?.id) payload.prospect_id = prospect.id;
            const res = await createDossier(payload);
            toast.success('Dossier créé');
            onOpenChange(false);
            onCreated && onCreated(res.data);
            if (andOpen && res.data?.id) {
                navigate(`/dossiers/${res.data.id}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="create-dossier-dialog">
                <DialogHeader>
                    <DialogTitle>Nouveau dossier</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="field-label">Titre du dossier *</Label>
                        <Input
                            value={form.titre}
                            onChange={(e) => setForm((p) => ({ ...p, titre: e.target.value }))}
                            placeholder="Ex : Rénovation salle de bain"
                            className="h-10 rounded-sm mt-1"
                            data-testid="dossier-titre-input"
                            autoFocus
                        />
                    </div>
                    <div>
                        <Label className="field-label">Type de travaux</Label>
                        <Input
                            value={form.type_travaux}
                            onChange={(e) => setForm((p) => ({ ...p, type_travaux: e.target.value }))}
                            placeholder="Ex : peinture, plomberie..."
                            className="h-10 rounded-sm mt-1"
                            data-testid="dossier-type-input"
                        />
                    </div>
                    <div>
                        <Label className="field-label">Montant devis (€ HT)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            value={form.montant_devis}
                            onChange={(e) => setForm((p) => ({ ...p, montant_devis: e.target.value }))}
                            placeholder="Optionnel"
                            className="h-10 rounded-sm mt-1"
                            data-testid="dossier-montant-input"
                        />
                    </div>
                    <div>
                        <Label className="field-label">Commentaire</Label>
                        <Textarea
                            rows={3}
                            value={form.commentaire}
                            onChange={(e) => setForm((p) => ({ ...p, commentaire: e.target.value }))}
                            className="rounded-sm mt-1"
                            placeholder="Notes initiales..."
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Annuler</Button>
                    <Button
                        onClick={() => handleSave(true)}
                        disabled={submitting}
                        className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm"
                        data-testid="dossier-create-submit"
                    >
                        {submitting ? 'Création...' : 'Enregistrer'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
